// Client-side search helpers for the Clients list.
//
// Behavior (P1 + P2 audit follow-up):
//   • Case-insensitive
//   • Whitespace-tolerant — the query is trimmed, then split on runs of
//     whitespace. Leading, trailing, and repeated spaces don't break search.
//   • Order-agnostic — every whitespace-delimited token in the query must
//     appear as a substring of the normalized haystack, but the token order
//     is free ("Rossi Marco" matches "Marco Rossi").
//   • Diacritics-folded — `Rôssi`/`ROSSI`/`rossi` all match the same rows.
//   • Empty (or whitespace-only) query matches every row.
//
// Search remains 100% client-side per the audit approval; no server-side
// `?q=`, no debounce, no Mongo text index. Existing multi-tenant isolation
// is untouched — this file only handles the local filter after
// `GET /api/clients` returns.

// Fold diacritics: "Rôssi" → "Rossi", "Biànchi" → "Bianchi".
// Uses Unicode normalization form D (canonical decomposition) then strips
// combining marks. Safe for ASCII input (no-op).
function normalize(text) {
    return String(text ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
}

// Tokenize the query into non-empty, diacritics-folded, lowercase tokens.
// Empty or whitespace-only input → [] (matches everything).
export function tokenizeQuery(query) {
    const q = normalize(query).trim();
    if (!q) return [];
    return q.split(/\s+/);
}

// Build a normalized haystack from a client row. Includes every field a
// user could reasonably type: company_name, contact_name, project_name,
// AND contact_email (audit P1 fix — was missing).
export function clientHaystack(client) {
    return normalize([
        client?.company_name,
        client?.contact_name,
        client?.project_name,
        client?.contact_email,
    ].filter(Boolean).join(" "));
}

// Return true iff every token in the (already-tokenized) query is a
// substring of the client's normalized haystack. Order-agnostic.
export function clientMatchesTokens(client, tokens) {
    if (!tokens.length) return true;
    const hay = clientHaystack(client);
    for (const t of tokens) {
        if (!hay.includes(t)) return false;
    }
    return true;
}

// Convenience one-shot used by Clients.jsx.
export function clientMatchesQuery(client, query) {
    return clientMatchesTokens(client, tokenizeQuery(query));
}
